import { API, graphqlOperation } from 'aws-amplify';
import Cookies from 'universal-cookie';

const SESSION_TOKEN_KEY = 'eop_session_token';
const SESSION_CONFLICT_KEY = 'eop_session_conflict';

/**
 * Store the session token after login.
 */
export function setSessionToken(token) {
    localStorage.setItem(SESSION_TOKEN_KEY, token);
}

/**
 * Get the current session token.
 */
export function getSessionToken() {
    return localStorage.getItem(SESSION_TOKEN_KEY);
}

/**
 * Remove the session token (on logout or session termination).
 */
export function clearSessionToken() {
    localStorage.removeItem(SESSION_TOKEN_KEY);
}

/**
 * Set the session conflict flag with a message.
 * This is read by the login page to show the conflict message.
 */
export function setSessionConflict(message) {
    localStorage.setItem(SESSION_CONFLICT_KEY, message);
}

/**
 * Get and clear the session conflict message (one-time read).
 */
export function getSessionConflictMessage() {
    const message = localStorage.getItem(SESSION_CONFLICT_KEY);
    localStorage.removeItem(SESSION_CONFLICT_KEY);
    return message;
}

/**
 * Check if a GraphQL error is a session expiry/conflict error.
 * Returns the error message if it is, null otherwise.
 */
export function checkSessionError(error) {
    if (!error || !error.errors) return null;

    for (const err of error.errors) {
        if (err.errorType === 'SessionExpired') {
            return err.message || 'Session expired';
        }
    }
    return null;
}

/**
 * Handle session termination — clear session, set conflict message, redirect to login.
 * Call this when a SessionExpired error is detected.
 */
export function handleSessionTermination(history, message) {
    const conflictMessage = message === 'Session has been invalidated'
        ? 'Your session has been terminated because your account was logged in from another location.'
        : message || 'Your session has expired. Please log in again.';

    clearSessionToken();
    setSessionConflict(conflictMessage);

    // Clear user cookies
    const cookies = new Cookies();
    cookies.remove('user_token', { path: '/' });
    cookies.remove('singpass_token', { path: '/' });
    cookies.remove('user-language', { path: '/' });

    // Redirect to login
    if (history) {
        history.push('/login');
    } else {
        window.location.href = '/login';
    }
}

/**
 * Wrapper for API.graphql that automatically handles session errors.
 * Use this instead of API.graphql directly for protected calls.
 *
 * Usage:
 *   import { secureGraphql } from '../infrastructure/SessionManager';
 *   const response = await secureGraphql(history, getUserProgressLambda, { userID: '...' });
 */
export async function secureGraphql(history, query, variables = {}) {
    try {
        const response = await API.graphql(graphqlOperation(query, variables));

        // Check for inline errors in the response
        if (response.errors) {
            const sessionError = checkSessionError(response);
            if (sessionError) {
                handleSessionTermination(history, sessionError);
                return null;
            }
        }

        return response;
    } catch (error) {
        // GraphQL errors from AppSync come as thrown errors
        const sessionError = checkSessionError(error);
        if (sessionError) {
            handleSessionTermination(history, sessionError);
            return null;
        }
        throw error;
    }
}
